// ============================================================================
// Why a sub is changing — one list, read from the building reason list.
// Item #667, approved by Ben 2026-10-02.
//
// The reasons live in facility_lifecycle_reasons under kind 'sub_change'; the
// screens that count lost buildings read sub_replacement_requests.reason_code,
// never the words. This module is the one place the app loads that list.
//
// ⛔ A failed load is surfaced, never turned into an empty list: an empty picker
// reads as "there are no reasons" and the form would refuse to submit with no
// explanation.
// ============================================================================

import { useEffect, useState } from 'react';
import { supabase } from '../../shared/supabase';
import { errorMessage } from '../../shared/errors';
import { subChangeOutcome, subChangeReasonShort } from './sub-change-reason-names';

export interface SubChangeReason {
  reason_code: string;
  label: string;
  /** 'Lost' counts against the outgoing sub on the tier screens. */
  default_intent: 'Lost' | 'Retained' | 'At_Risk';
  /** false = past records only (reason not recorded); never offered. */
  is_active: boolean;
  sort_order: number;
}

export { SUB_CHANGE_REASON_HELP, subChangeReasonShort } from './sub-change-reason-names';

let cache: Promise<SubChangeReason[]> | null = null;

export function loadSubChangeReasons(): Promise<SubChangeReason[]> {
  if (!cache) {
    cache = (async () => {
      const { data, error } = await supabase
        .from('facility_lifecycle_reasons')
        .select('reason_code, label, default_intent, is_active, sort_order')
        .eq('kind', 'sub_change')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as SubChangeReason[];
      if (rows.length === 0) throw new Error('The sub-change reason list came back empty.');
      return rows;
    })();
    // A failure is not cached: the next caller tries again.
    cache.catch(() => {
      cache = null;
    });
  }
  return cache;
}

export function useSubChangeReasons(): {
  reasons: SubChangeReason[];
  /** What a person may pick for a NEW change. */
  offered: SubChangeReason[];
  loading: boolean;
  error: string | null;
} {
  const [reasons, setReasons] = useState<SubChangeReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadSubChangeReasons()
      .then((r) => live && setReasons(r))
      .catch((e) => live && setError(errorMessage(e)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, []);
  return { reasons, offered: reasons.filter((r) => r.is_active), loading, error };
}

/** The chip shown above the words on the request list, detail page and approval
 *  queue. Renders nothing for a record that has no reason yet. */
export function SubChangeReasonTag({
  code,
  reasons,
}: {
  code: string | null | undefined;
  reasons: SubChangeReason[];
}) {
  if (!code) return null;
  const r = reasons.find((x) => x.reason_code === code);
  const counts = r ? r.default_intent === 'Lost' : true;
  const unknown = code === 'sub_reason_not_recorded';
  const tone = unknown
    ? 'text-st-warn-fg'
    : counts
      ? 'text-st-danger-fg'
      : 'text-tcs-text-secondary';
  return (
    <span
      className={`inline-block rounded bg-tcs-surface px-1.5 py-0.5 text-[11px] font-medium ${tone}`}
      title={subChangeOutcome(code)}
    >
      {r?.label ?? subChangeReasonShort(code)}
    </span>
  );
}
